import { isFixedAssetErrorCode } from '@/api/handlers/resources/fixedAssets';
import type { FixedAssetStatusFilter } from '@/api/handlers/resources/fixedAssets/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type {
    AssetCategory,
    AssetEntryLinkKind,
    DepreciationRunLine,
    DepreciationRunLineReason,
    DepreciationScheduleRow,
    FixedAsset,
    FixedAssetReconciliation,
    FixedAssetReconciliationLine,
    FixedAssetRegisterSummary,
    FixedAssetStatus,
    LocaleCode,
    PlausibilityWarning,
} from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import {
    isoToDisplayDate,
    NIL_AMOUNT,
    resolveLabel,
} from '@/pages/accounting/statements/statementMeta';
import { monthLabelKey } from '@/pages/accounting/configurations/periods/periodsMeta';

/**
 * Pure rules and presentational config for the Fixed Assets register.
 *
 * Everything derived lives here so the views stay render-only, and so the two
 * things that must not be got wrong — the tie-out and the NBV identity — are
 * plain functions with tests rather than JSX.
 */

// ── status ──────────────────────────────────────────────────────────────────

/**
 * Status pill styling in the journal's calm vocabulary. **A disposal is ordinary
 * bookkeeping, not an error**: Disposed reads as a settled, neutral state, never
 * as danger.
 */
export const ASSET_STATUS_CHIP: Record<
    FixedAssetStatus,
    { className: string; labelKey: string }
> = {
    ACTIVE: {
        className: 'bg-success-soft text-success-soft-foreground',
        labelKey: 'fixedAssets.status.active',
    },
    DISPOSED: {
        className: 'bg-surface-secondary text-muted',
        labelKey: 'fixedAssets.status.disposed',
    },
};

/** i18n key for a depreciation method (only straight-line exists today). */
export const methodLabelKey = (method: string): string =>
    `fixedAssets.method.${method}`;

/**
 * The status filter's options, in the order they appear. Config, not markup —
 * the project keeps presentational maps in `.ts` so the views stay render-only.
 */
export const ASSET_STATUS_FILTERS: {
    id: FixedAssetStatusFilter;
    labelKey: string;
}[] = [
    { id: 'ACTIVE', labelKey: 'fixedAssets.filters.active' },
    { id: 'DISPOSED', labelKey: 'fixedAssets.filters.disposed' },
    { id: 'ALL', labelKey: 'fixedAssets.filters.all' },
];

/** i18n key naming the reason a posted entry touched an asset. */
export const ENTRY_KIND_KEY: Record<AssetEntryLinkKind, string> = {
    ACQUISITION: 'fixedAssets.detail.entryKind.acquisition',
    DEPRECIATION: 'fixedAssets.detail.entryKind.depreciation',
    DISPOSAL: 'fixedAssets.detail.entryKind.disposal',
};

/** Copy + icon for each way the register can come back with no rows. */
export const REGISTER_EMPTY_COPY: Record<
    Exclude<RegisterEmptyState, 'NONE'>,
    { titleKey: string; bodyKey: string; icon: string }
> = {
    NO_ASSETS: {
        titleKey: 'fixedAssets.empty.noAssets.title',
        bodyKey: 'fixedAssets.empty.noAssets.body',
        icon: 'gravity-ui:box',
    },
    // An all-disposed register is not empty — it is a register whose assets have
    // all reached the end of their life. Saying "No assets yet" would be untrue.
    ALL_DISPOSED: {
        titleKey: 'fixedAssets.empty.allDisposed.title',
        bodyKey: 'fixedAssets.empty.allDisposed.body',
        icon: 'gravity-ui:archive',
    },
    NO_MATCHES: {
        titleKey: 'fixedAssets.empty.noMatches.title',
        bodyKey: 'fixedAssets.empty.noMatches.body',
        icon: 'gravity-ui:magnifier',
    },
};

/** Shared header cell classes for the register table. */
export const REGISTER_TEXT_HEADER =
    'px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-muted';
export const REGISTER_NUMERIC_HEADER =
    'px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-muted';

/**
 * i18n key explaining an asset's contribution to a run.
 *
 * Five of these explain a **zero**, and they are deliberately distinct: an asset
 * whose accumulated depreciation already covers this period (`ALREADY_CHARGED`)
 * has years of life left, and telling the user it is `FULLY_DEPRECIATED` or that
 * its `LIFE_ENDED` would be false.
 *
 * `CATCH_UP` is the odd one and the reason this map is no longer zero-only: it
 * explains a **non-zero** charge that is larger than a month's instalment,
 * because earlier periods were never run. A ₾3,000 month with no explanation
 * reads as a bug; the count in {@link toRunPreviewRows} is what makes it read as
 * a correct run instead.
 */
// `OK` is absent by design: a row that ran normally carries no
// explanation (`reasonKey` is null), and a key mapped to an empty
// string is a label waiting to be rendered blank.
export const RUN_REASON_KEY: Record<
    Exclude<DepreciationRunLineReason, 'OK'>,
    string
> = {
    NOT_IN_SERVICE: 'fixedAssets.run.reasons.notInService',
    ALREADY_CHARGED: 'fixedAssets.run.reasons.alreadyCharged',
    FULLY_DEPRECIATED: 'fixedAssets.run.reasons.fullyDepreciated',
    LIFE_ENDED: 'fixedAssets.run.reasons.lifeEnded',
    DISPOSED: 'fixedAssets.run.reasons.disposed',
    CATCH_UP: 'fixedAssets.run.reasons.catchUp',
};

// ── periods ─────────────────────────────────────────────────────────────────

/** The `YYYY-MM` period an ISO date falls in. */
export const periodFromDate = (isoDate: string): string => isoDate.slice(0, 7);

/**
 * `<Month> <Year>` for a `YYYY-MM`, using the app's own month strings.
 *
 * Deliberately not `Intl` and not dayjs: dayjs is loaded with no locale packs
 * (so `ka` silently renders English) and a Chromium build verified in testing
 * resolved `ka` to `en-US`. The twelve strings already live in the locale files
 * under `periods.months.*` — reuse them (see `periodsMeta`).
 */
export const formatPeriod = (
    period: string,
    t: (key: string) => string,
): string => `${t(monthLabelKey(period))} ${period.slice(0, 4)}`;

// ── the tie-out ─────────────────────────────────────────────────────────────

/**
 * One side of the tie-out, straight from `GET /reconciliation`.
 *
 * **Nothing here is recomputed.** The server holds the register and the ledger
 * and reports both figures plus its own difference; subtracting them again
 * client-side would be a second opinion about the one number the tie-out exists
 * to state, and two figures fetched at different moments can differ by a race
 * rather than by a real gap.
 *
 * This replaced a client-side comparison against the trial balance, and with it
 * the `isResolved` / *pending integration* state: that existed because the
 * register was fabricated and had nothing real to reconcile against. Both sides
 * are live now, so the bar reports agreement or a genuine difference — and a
 * difference, when it appears, is a finding rather than a defect in the screen.
 */
export type TieOutSide = {
    accountCode: string;
    accountName: string;
    registerTetri: number;
    ledgerTetri: number;
    /** The server's `register − ledger`. Zero on every line when it ties out. */
    differenceTetri: number;
    agrees: boolean;
};

export type RegisterTieOut = {
    cost: TieOutSide[];
    accumulatedDepreciation: TieOutSide[];
    /** The **server's** verdict, not a re-derivation from the lines. */
    inAgreement: boolean;
    /** Advisory notices — `REGISTER_NE_LEDGER` when a side disagrees. */
    warnings: PlausibilityWarning[];
    /** Account codes the comparison read, for the "compared against" caption. */
    costAccountCodes: string[];
    accumulatedDepreciationAccountCodes: string[];
};

const toTieOutSide = (line: FixedAssetReconciliationLine): TieOutSide => ({
    accountCode: line.accountCode,
    accountName: line.accountName,
    registerTetri: line.registerTetri,
    ledgerTetri: line.ledgerTetri,
    differenceTetri: line.differenceTetri,
    agrees: line.differenceTetri === 0,
});

/**
 * The register-to-ledger tie-out, as the server reported it.
 *
 * Returns `null` while the reconciliation is still loading — an unknown ledger
 * is not the same as a disagreeing one and must never be drawn as a difference.
 */
export const toTieOut = (
    reconciliation: FixedAssetReconciliation | null,
): RegisterTieOut | null => {
    if (reconciliation === null) {
        return null;
    }

    return {
        cost: reconciliation.cost.map(toTieOutSide),
        accumulatedDepreciation:
            reconciliation.accumulatedDepreciation.map(toTieOutSide),
        inAgreement: reconciliation.tiesOut,
        warnings: reconciliation.warnings,
        costAccountCodes: reconciliation.cost.map((line) => line.accountCode),
        accumulatedDepreciationAccountCodes:
            reconciliation.accumulatedDepreciation.map(
                (line) => line.accountCode,
            ),
    };
};

/** Total a side of the tie-out for the summary figures the bar shows. */
export const sumSide = (
    sides: TieOutSide[],
    field: 'registerTetri' | 'ledgerTetri',
): number => sides.reduce((total, side) => total + side[field], 0);

/**
 * How far a side is out, as a **non-cancelling** magnitude.
 *
 * Deliberately the sum of absolute differences, not the difference of the
 * totals. A side split across two accounts that are +₾100 and −₾100 is out by
 * ₾200 on the books and by ₾0 on a signed sum — and a bar that says
 * "Out of agreement · Off by ₾0.00" has reported a discrepancy that reads as no
 * discrepancy, which is the standing rule inverted. This can never be zero while
 * any line disagrees.
 */
export const disagreementTetri = (sides: TieOutSide[]): number =>
    sides.reduce((total, side) => total + Math.abs(side.differenceTetri), 0);

/** The lines actually out, so the bar can name them rather than only total them. */
export const disagreeingLines = (sides: TieOutSide[]): TieOutSide[] =>
    sides.filter((side) => !side.agrees);

// ── register rows ───────────────────────────────────────────────────────────

/** A render-ready register row. All derivation happens here, none in the View. */
export type AssetRow = {
    id: string;
    /** 1-based position in the visible list (the `#` column). */
    index: number;
    name: string;
    categoryLabel: string;
    acquisitionDateText: string;
    costText: string;
    accumulatedText: string;
    netBookValueText: string;
    methodLabelKey: string;
    usefulLifeMonths: number;
    status: FixedAssetStatus;
    isDisposed: boolean;
};

/** Category id → localized name, for the register's Category column. */
export const categoryLabelMap = (
    categories: AssetCategory[],
    locale: LocaleCode,
): Map<string, string> =>
    new Map(
        categories.map((category) => [
            category.id,
            resolveLabel(category.name, locale, category.id),
        ]),
    );

/**
 * Map assets to rows. Nil amounts read as an en dash rather than `₾ 0.00`, so a
 * brand-new asset's accumulated column says "nothing has been charged" instead
 * of asserting a zero.
 */
export const toAssetRows = (
    assets: FixedAsset[],
    categories: AssetCategory[],
    locale: LocaleCode,
): AssetRow[] => {
    const labels = categoryLabelMap(categories, locale);
    return assets.map((asset, index) => ({
        id: asset.id,
        index: index + 1,
        name: resolveLabel(asset.name, locale, asset.id),
        categoryLabel: labels.get(asset.categoryId) ?? asset.categoryId,
        acquisitionDateText: isoToDisplayDate(asset.acquisitionDate),
        costText: formatGel(asset.costTetri),
        accumulatedText:
            asset.accumulatedDepreciationTetri === 0
                ? NIL_AMOUNT
                : formatGel(asset.accumulatedDepreciationTetri),
        netBookValueText: formatGel(asset.netBookValueTetri),
        methodLabelKey: methodLabelKey(asset.method),
        usefulLifeMonths: asset.usefulLifeMonths,
        status: asset.status,
        isDisposed: asset.status === 'DISPOSED',
    }));
};

/** Summary card figures, pre-formatted. */
export type SummaryFigures = {
    assetCount: number;
    totalCostText: string;
    totalAccumulatedText: string;
    totalNetBookValueText: string;
};

export const toSummaryFigures = (
    summary: FixedAssetRegisterSummary,
): SummaryFigures => ({
    assetCount: summary.assetCount,
    totalCostText: formatGel(summary.totalCostTetri),
    totalAccumulatedText: formatGel(summary.totalAccumulatedDepreciationTetri),
    totalNetBookValueText: formatGel(summary.totalNetBookValueTetri),
});

/**
 * Which empty-ish state the register is in.
 *
 * An **all-disposed** register is not empty — it holds real history and the user
 * has simply filtered or written everything off. Saying "No assets yet" there
 * would be a lie, so it gets its own state.
 */
export type RegisterEmptyState =
    'NONE' | 'NO_ASSETS' | 'ALL_DISPOSED' | 'NO_MATCHES';

export const registerEmptyState = (params: {
    visibleCount: number;
    activeCount: number;
    hasSearch: boolean;
    /** The register holds nothing at all, whatever the filter says. */
    totalCount: number;
}): RegisterEmptyState => {
    if (params.visibleCount > 0) {
        return 'NONE';
    }
    if (params.hasSearch) {
        return 'NO_MATCHES';
    }
    if (params.totalCount === 0) {
        return 'NO_ASSETS';
    }
    return params.activeCount === 0 ? 'ALL_DISPOSED' : 'NO_MATCHES';
};

// ── depreciation-run preview ────────────────────────────────────────────────

/** A render-ready preview row: the charge, or the zero and why. */
export type RunPreviewRow = {
    assetId: string;
    name: string;
    amountText: string;
    /**
     * The stated reason, when there is one to state. Null for an ordinary `OK`
     * charge — a normal month needs no explanation, and labelling every row
     * would bury the ones that do.
     */
    reasonKey: string | null;
    /**
     * Interpolation for `reasonKey`. Carries `{ count }` on a catch-up, so the
     * label can read "catching up 3 months" rather than just "catching up".
     * Empty when the count could not be derived — an unqualified "catching up"
     * is honest; a wrong number is not.
     */
    reasonValues: { count?: number };
    isZero: boolean;
    /** A charge covering several months at once. Correct, but worth flagging. */
    isCatchUp: boolean;
};

/**
 * Run lines → render-ready rows.
 *
 * A row is explained whenever the reason is anything but a plain `OK`: the five
 * zero reasons say why nothing was charged, and `CATCH_UP` says why *more* than
 * a month was.
 */
export const toRunPreviewRows = (
    lines: DepreciationRunLine[],
    locale: LocaleCode,
): RunPreviewRow[] =>
    lines.map((line) => {
        const isZero = line.amountTetri === 0;
        const isCatchUp = line.reason === 'CATCH_UP';
        // A catch-up with a known count gets the counted phrasing; without one it
        // falls back to the bare label. An unqualified "catching up" is honest;
        // a fabricated number is not.
        const hasCount = isCatchUp && line.catchUpInstalments !== null;
        const reasonKey =
            line.reason === 'OK'
                ? null
                : hasCount
                  ? 'fixedAssets.run.reasons.catchUpCounted'
                  : RUN_REASON_KEY[line.reason];

        return {
            assetId: line.assetId,
            name: resolveLabel(line.assetName, locale, line.assetId),
            amountText: isZero ? NIL_AMOUNT : formatGel(line.amountTetri),
            reasonKey,
            reasonValues: hasCount
                ? { count: line.catchUpInstalments as number }
                : {},
            isZero,
            isCatchUp,
        };
    });

// ── depreciation schedule (detail drawer) ───────────────────────────────────

export type ScheduleRow = {
    period: string;
    periodText: string;
    amountText: string;
    accumulatedText: string;
    netBookValueText: string;
    posted: boolean;
    journalEntryId: string | null;
};

export const toScheduleRows = (
    rows: DepreciationScheduleRow[],
    t: (key: string) => string,
): ScheduleRow[] =>
    rows.map((row) => ({
        period: row.period,
        periodText: formatPeriod(row.period, t),
        amountText: formatGel(row.amountTetri),
        accumulatedText: formatGel(row.accumulatedTetri),
        netBookValueText: formatGel(row.netBookValueTetri),
        posted: row.posted,
        journalEntryId: row.journalEntryId,
    }));

// ── disposal ────────────────────────────────────────────────────────────────

/** Which way a disposal's result points. `NIL` = it broke even. */
export type DisposalDirection = 'GAIN' | 'LOSS' | 'NIL';

export const disposalDirection = (
    gainOrLossTetri: number,
): DisposalDirection =>
    gainOrLossTetri > 0 ? 'GAIN' : gainOrLossTetri < 0 ? 'LOSS' : 'NIL';

/**
 * The gain or loss a disposal will book, computed client-side so the dialog can
 * show it live as the user types the proceeds.
 *
 * `proceeds − netBookValue`. The server recomputes it authoritatively when the
 * draft is prepared; this is the same arithmetic, in tetri, for the preview.
 */
export const computeGainOrLossTetri = (
    netBookValueTetri: number,
    proceedsTetri: number,
): number => proceedsTetri - netBookValueTetri;

/** i18n key for the disposal-result headline. */
export const DISPOSAL_DIRECTION_KEY: Record<DisposalDirection, string> = {
    GAIN: 'fixedAssets.dispose.gain',
    LOSS: 'fixedAssets.dispose.loss',
    NIL: 'fixedAssets.dispose.breakEven',
};

// ── refusals ────────────────────────────────────────────────────────────────

/**
 * Turn a refusal into something a bookkeeper can act on.
 *
 * **No raw 400 may reach the user**, and there are three ways one can:
 *
 * 1. A **domain refusal** — `ApiError { error, code }`. Eighteen codes were
 *    measured on this surface (the brief named nine); each gets its own message
 *    under `fixedAssets.errors.*`, because "the request was invalid" is not an
 *    answer to "why can't I record this disposal?".
 * 2. A **model-binding failure** — ASP.NET ProblemDetails with **no `code`** and
 *    an empty `message`, because the text sits in `title` and the field errors in
 *    `errors`. A handler that only switches on `code` renders nothing at all for
 *    these, so the field errors are used next.
 * 3. Anything else — the server's own prose, and only if there is none, a generic
 *    fallback. The server's message is preferred over a generic one wherever it
 *    exists: it names the field and the figure, which no static string can.
 *
 * An unknown code is not a failure: it falls through to the server's prose,
 * which is English but specific. A new server code shows up as an unlocalized
 * sentence rather than as "something went wrong".
 */
export const fixedAssetErrorMessage = (
    error: Pick<ErrorResponse, 'code' | 'message' | 'errors'>,
    t: (key: string, values?: Record<string, unknown>) => string,
): string => {
    if (isFixedAssetErrorCode(error.code)) {
        return t(`fixedAssets.errors.${error.code}`);
    }

    if (error.message) {
        return error.message;
    }

    // ProblemDetails: `errors` is `{ FieldName: ["why"] }`. The first sentence is
    // the actionable one; joining them all produces a wall of near-duplicates.
    const fieldErrors = error.errors;
    if (fieldErrors) {
        for (const value of Object.values(fieldErrors)) {
            const first = Array.isArray(value) ? value[0] : value;
            if (typeof first === 'string' && first) {
                return first;
            }
        }
    }

    return t('fixedAssets.errors.generic');
};

/**
 * The advisory a disposal can raise, and the reason it is *shown* rather than
 * suppressed: account 4300 is typed Revenue, so a loss debits revenue and reads
 * as negative other income rather than as an expense.
 *
 * It is **non-blocking by design**. The accounting question behind it is open
 * with the accountants, and a client that hid it would be deciding the answer by
 * omission. Rendered as an advisory beside the figures, never as a validation
 * error and never as something to dismiss before continuing.
 */
export const isBlockingWarning = (_warning: PlausibilityWarning): boolean =>
    false;

/**
 * The two kinds of asset the create form offers, in the order they appear.
 *
 * Config, not markup — the project keeps presentational maps in `.ts` so the
 * views stay render-only. Acquisition is first because it is both the common
 * case and the safe default: it posts, and a posting that should not have
 * happened is visible in the journal, whereas a missing one surfaces only as a
 * tie-out difference later.
 */
export const KIND_OPTIONS: {
    value: 'ACQUISITION' | 'OPENING';
    /** Literal testid discriminator — never a dynamic id. */
    slot: string;
    labelKey: string;
    hintKey: string;
}[] = [
    {
        value: 'ACQUISITION',
        slot: 'acquisition',
        labelKey: 'fixedAssets.form.kind.acquisition',
        hintKey: 'fixedAssets.form.kind.acquisitionHint',
    },
    {
        value: 'OPENING',
        slot: 'opening',
        labelKey: 'fixedAssets.form.kind.opening',
        hintKey: 'fixedAssets.form.kind.openingHint',
    },
];
